import { useEffect } from 'react';

/**
 * Sets document.title + meta tags declaratively per route.
 * Restores previous values on unmount so React Router transitions stay clean.
 */
export default function useDocumentMeta({ title, description, canonical, ogImage, ogType = 'website' }) {
  useEffect(() => {
    const prevTitle = document.title;
    const previousValues = {};

    if (title) document.title = title;

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        previousValues[`${attr}=${key}`] = { el, created: true };
      } else {
        previousValues[`${attr}=${key}`] = { el, prev: el.getAttribute('content'), created: false };
      }
      el.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    // Open Graph
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonical || (typeof window !== 'undefined' ? window.location.href : ''));
    if (ogImage) setMeta('property', 'og:image', ogImage);
    // Twitter
    setMeta('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    if (ogImage) setMeta('name', 'twitter:image', ogImage);

    // Canonical link
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    const canonicalPrev = canonicalEl?.getAttribute('href');
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
    }

    return () => {
      document.title = prevTitle;
      Object.values(previousValues).forEach(({ el, prev, created }) => {
        if (created && el.parentNode) el.parentNode.removeChild(el);
        else if (prev != null) el.setAttribute('content', prev);
      });
      if (canonicalEl && canonicalPrev != null) canonicalEl.setAttribute('href', canonicalPrev);
    };
  }, [title, description, canonical, ogImage, ogType]);
}
